/**
 * Cities Recovry serves, expanding across PAN India. Slug is the URL segment
 * used in /physiotherapy-in-:city, /:service-in-:city etc. A city with no
 * verified clinic nearby is gated out of prerender + sitemap automatically
 * (see src/seo/inventoryGate.ts), so it is safe to add cities ahead of supply.
 *
 * Adding a new city is intentionally low-effort: append one entry here and
 * its SEO location pages, sitemap entries and internal links are generated
 * automatically.
 *
 * lat/lng = approximate city centroid, used for "near me" sorts and as
 * a fallback when sector geo is missing.
 */

export interface City {
  slug: string;
  name: string;
  state: string;
  lat: number;
  lng: number;
  /** Short blurb used in hero/intro sections - 1-2 sentences. */
  intro: string;
  /** What patients in this city commonly come in for. Drives city-flavored copy. */
  commonAilments: string[];
}

export const CITIES: City[] = [
  {
    slug: 'noida',
    name: 'Noida',
    state: 'Uttar Pradesh',
    lat: 28.5355,
    lng: 77.3910,
    intro:
      'Noida is one of NCR\'s biggest IT and corporate hubs, with hundreds of thousands of desk-bound professionals living and working across its sectors. Recovry-verified physiotherapy and rehab centres here treat the full range of musculoskeletal, sports and neurological conditions.',
    commonAilments: [
      'Desk-job neck and back pain',
      'Posture and ergonomic issues',
      'Sports and gym injuries',
      'Sciatica and lumbar pain',
      'Frozen shoulder',
    ],
  },
  {
    slug: 'ghaziabad',
    name: 'Ghaziabad',
    state: 'Uttar Pradesh',
    lat: 28.6692,
    lng: 77.4538,
    intro:
      'Ghaziabad is a fast-growing residential and commercial city in NCR, home to Indirapuram, Vaishali, Vasundhara and Raj Nagar Extension. Recovry-verified physiotherapy clinics here cover orthopedic rehab, sports recovery and women\'s health physiotherapy.',
    commonAilments: [
      'Lower back pain',
      'Knee pain in older adults',
      'Postpartum recovery',
      'Cervical spondylosis',
      'Plantar fasciitis',
    ],
  },
  {
    slug: 'delhi',
    name: 'Delhi',
    state: 'Delhi',
    lat: 28.6139,
    lng: 77.2090,
    intro:
      'Delhi has India\'s densest network of healthcare providers, including specialised physiotherapy clinics across South Delhi, Saket, Vasant Kunj, Dwarka, Rohini and East Delhi. Find Recovry-verified centres for orthopedic, neurological and sports physiotherapy.',
    commonAilments: [
      'Cervical and lumbar spine pain',
      'Stroke rehabilitation',
      'Knee replacement recovery',
      'Sports injuries',
      'Geriatric mobility',
    ],
  },
  {
    slug: 'gurgaon',
    name: 'Gurgaon',
    state: 'Haryana',
    lat: 28.4595,
    lng: 77.0266,
    intro:
      'Gurgaon (Gurugram) is NCR\'s corporate capital with a high concentration of MNC employees, premium gyms and active lifestyle communities. Recovry-verified physio centres here specialise in sports rehab, post-surgical care and ergonomic correction.',
    commonAilments: [
      'Tech-neck and shoulder strain',
      'Running and cycling injuries',
      'ACL and knee rehab',
      'Tennis and golfer\'s elbow',
      'Stress-related muscular pain',
    ],
  },
  {
    slug: 'faridabad',
    name: 'Faridabad',
    state: 'Haryana',
    lat: 28.4089,
    lng: 77.3178,
    intro:
      'Faridabad is a major industrial and residential city of Haryana with a growing footprint of physiotherapy and rehab clinics. Recovry-verified centres here cover back pain, knee pain, post-fracture rehab and home physiotherapy.',
    commonAilments: [
      'Industrial and lifting injuries',
      'Lower back pain',
      'Post-fracture recovery',
      'Arthritis management',
      'Diabetic neuropathy support',
    ],
  },
  {
    slug: 'ranchi',
    name: 'Ranchi',
    state: 'Jharkhand',
    lat: 23.3441,
    lng: 85.3096,
    intro:
      'Ranchi, the capital of Jharkhand, has a fast-growing demand for quality physiotherapy across its residential and institutional hubs. Recovry-verified physiotherapy and rehab centres here treat orthopedic, sports, neurological and post-surgical conditions, with home-visit options available.',
    commonAilments: [
      'Lower back and neck pain',
      'Knee and joint pain',
      'Post-fracture and post-surgery rehab',
      'Stroke and neurological rehabilitation',
      'Sports and gym injuries',
    ],
  },
  {
    slug: 'jamshedpur',
    name: 'Jamshedpur',
    state: 'Jharkhand',
    lat: 22.8046,
    lng: 86.2029,
    intro:
      'Jamshedpur, Jharkhand\'s industrial city, has a large working population prone to musculoskeletal and lifting-related strain. Recovry-verified centres here cover orthopedic rehab, sports recovery, occupational injury care and home physiotherapy.',
    commonAilments: [
      'Industrial and lifting injuries',
      'Lower back pain',
      'Frozen shoulder',
      'Arthritis management',
      'Post-fracture recovery',
    ],
  },
  {
    slug: 'chandigarh',
    name: 'Chandigarh',
    state: 'Chandigarh',
    lat: 30.7333,
    lng: 76.7794,
    intro:
      'Chandigarh, the planned city and tricity hub, has an active, health-conscious population and strong demand for specialised rehab. Recovry-verified physiotherapy centres here provide sports rehab, post-surgical care, neurological rehabilitation and home physiotherapy.',
    commonAilments: [
      'Sports and running injuries',
      'Cervical and lumbar spine pain',
      'ACL and knee rehab',
      'Post-surgical rehabilitation',
      'Geriatric mobility support',
    ],
  },
  {
    slug: 'bengaluru',
    name: 'Bengaluru',
    state: 'Karnataka',
    lat: 12.9716,
    lng: 77.5946,
    intro:
      'Bengaluru, India\'s technology capital, has one of the country\'s youngest and most desk-bound working populations, driving high demand for posture, sports and rehabilitation physiotherapy. Recovry-verified physiotherapy and rehab centres across the city treat orthopedic, sports, neurological and post-surgical conditions, with home-visit options available across most localities.',
    commonAilments: [
      'Tech-neck and desk-job back pain',
      'Running, cycling and CrossFit injuries',
      'ACL and post-surgical knee rehab',
      'Cervical and lumbar spine pain',
      'Posture and ergonomic correction',
    ],
  },
  {
    slug: 'lucknow',
    name: 'Lucknow',
    state: 'Uttar Pradesh',
    lat: 26.8467,
    lng: 80.9462,
    intro:
      'Lucknow is Uttar Pradesh\'s capital and a fast-growing medical hub, with private clinics clustered in Gomti Nagar, Hazratganj, Indira Nagar and Aliganj. Recovry-verified physiotherapy centres here cover orthopedic rehab, spine care and post-surgical recovery.',
    commonAilments: [
      'Lower back and cervical pain',
      'Knee osteoarthritis',
      'Post-fracture rehabilitation',
      'Frozen shoulder',
      'Sciatica',
    ],
  },
  {
    slug: 'mumbai',
    name: 'Mumbai',
    state: 'Maharashtra',
    lat: 19.0760,
    lng: 72.8777,
    intro:
      'Mumbai\'s long commutes and dense high-rise living make back, neck and knee complaints everyday problems, from Kandivali and Andheri in the western suburbs to Bandra and South Mumbai. Recovry-verified physiotherapists here offer clinic visits and home physiotherapy across the city.',
    commonAilments: [
      'Commute-related back and neck pain',
      'Knee pain and osteoarthritis',
      'Sports and running injuries',
      'Post-surgical rehabilitation',
      'Home physiotherapy for seniors',
    ],
  },
  {
    slug: 'nashik',
    name: 'Nashik',
    state: 'Maharashtra',
    lat: 19.9975,
    lng: 73.7898,
    intro:
      'Nashik\'s physiotherapy demand comes from its vineyard and industrial workforce in Satpur and Ambad, a large older population in Panchavati and Gangapur Road, and weekend trekkers heading to the Sahyadri hills. Recovry-verified physiotherapists here treat back, knee and post-surgical cases, with home visits across the city.',
    commonAilments: [
      'Lower back pain from standing and factory work',
      'Knee osteoarthritis in older adults',
      'Post-fracture and post-surgical rehabilitation',
      'Cervical spondylosis',
      'Trekking and sports injuries',
    ],
  },
  {
    slug: 'ahmedabad',
    name: 'Ahmedabad',
    state: 'Gujarat',
    lat: 23.0225,
    lng: 72.5714,
    intro:
      'Ahmedabad has a strong private healthcare network along SG Highway, Thaltej, Satellite and Bopal, with growing demand in Maninagar and Odhav on the east side. Recovry-verified physiotherapy clinics here treat orthopedic, sports and neurological conditions, with home-visit options.',
    commonAilments: [
      'Lower back pain',
      'Knee pain in older adults',
      'Cervical spondylosis',
      'Post-surgical rehabilitation',
      'Stroke and paralysis rehab',
    ],
  },
  {
    slug: 'vadodara',
    name: 'Vadodara',
    state: 'Gujarat',
    lat: 22.3072,
    lng: 73.1812,
    intro:
      'Vadodara is a compact industrial and university city where Gorwa, Alkapuri and Manjalpur hold most of the private clinics. Recovry-verified physiotherapy centres here focus on orthopedic rehab, sports injuries and mobility care for seniors.',
    commonAilments: [
      'Back and neck pain',
      'Knee pain',
      'Sports injuries',
      'Post-fracture rehabilitation',
      'Geriatric mobility',
    ],
  },
  {
    slug: 'surat',
    name: 'Surat',
    state: 'Gujarat',
    lat: 21.1702,
    lng: 72.8311,
    intro:
      'Surat\'s textile and diamond workforce spends long hours seated or standing at machines, which shows up as back, neck and shoulder complaints. Recovry-verified physiotherapy clinics here treat work-related strain, sports injuries and post-surgical recovery.',
    commonAilments: [
      'Work-related back and shoulder pain',
      'Cervical spondylosis',
      'Knee pain',
      'Sciatica',
      'Post-surgical rehabilitation',
    ],
  },
  {
    slug: 'dehradun',
    name: 'Dehradun',
    state: 'Uttarakhand',
    lat: 30.3165,
    lng: 78.0322,
    intro:
      'Dehradun is Uttarakhand\'s capital and a retirement and education hub, with clinics concentrated around Rajpur Road, Turner Road and Clement Town. Recovry-verified physiotherapy centres here focus on joint pain, spine care and mobility for older adults.',
    commonAilments: [
      'Knee osteoarthritis',
      'Lower back pain',
      'Geriatric mobility',
      'Post-fracture rehabilitation',
      'Neck and shoulder pain',
    ],
  },
  {
    slug: 'rourkela',
    name: 'Rourkela',
    state: 'Odisha',
    lat: 22.2604,
    lng: 84.8536,
    intro:
      'Rourkela is a steel city where heavy industrial work and long shifts drive demand for back, knee and shoulder rehabilitation, with clinics in Chhend, Civil Township and Panposh. Recovry-verified physiotherapy centres here treat orthopedic, sports and neurological conditions.',
    commonAilments: [
      'Occupational back pain',
      'Knee pain',
      'Shoulder injuries',
      'Post-surgical rehabilitation',
      'Stroke rehabilitation',
    ],
  },
  {
    slug: 'alwar',
    name: 'Alwar',
    state: 'Rajasthan',
    lat: 27.5530,
    lng: 76.6346,
    intro:
      'Alwar serves a large rural and semi-urban catchment on the Rajasthan side of NCR, where patients often travel far for specialist rehab. Recovry-verified physiotherapy clinics here cover joint pain, spine care and recovery after fractures and surgery.',
    commonAilments: [
      'Knee and hip pain',
      'Lower back pain',
      'Post-fracture rehabilitation',
      'Cervical spondylosis',
      'Paralysis rehabilitation',
    ],
  },
  {
    slug: 'rohtak',
    name: 'Rohtak',
    state: 'Haryana',
    lat: 28.8955,
    lng: 76.6066,
    intro:
      'Rohtak is Haryana\'s medical education hub, anchored by PGIMS, and draws patients from surrounding districts for rehabilitation. Recovry-verified physiotherapy clinics here treat sports injuries from the city\'s strong wrestling and athletics culture alongside spine and joint conditions.',
    commonAilments: [
      'Sports and wrestling injuries',
      'Lower back pain',
      'Knee pain',
      'Post-surgical rehabilitation',
      'Cervical pain',
    ],
  },
  {
    slug: 'karnal',
    name: 'Karnal',
    state: 'Haryana',
    lat: 29.6857,
    lng: 76.9905,
    intro:
      'Karnal sits on the Delhi-Chandigarh highway with a mix of farming families and a growing urban middle class, and its clinics see a lot of joint and spine complaints from physical work. Recovry-verified physiotherapy centres here handle orthopedic rehab, neuro rehab and geriatric care.',
    commonAilments: [
      'Knee pain from physical work',
      'Lower back pain',
      'Cervical spondylosis',
      'Post-fracture rehabilitation',
      'Stroke rehabilitation',
    ],
  },
  {
    slug: 'jalandhar',
    name: 'Jalandhar',
    state: 'Punjab',
    lat: 31.3260,
    lng: 75.5762,
    intro:
      'Jalandhar is Punjab\'s sports-goods capital with a deep athletics culture, so sports injury rehab sits alongside the usual spine and joint care. Recovry-verified physiotherapy clinics here treat ligament injuries, back pain and post-surgical recovery.',
    commonAilments: [
      'Sports and ligament injuries',
      'Lower back pain',
      'Knee pain',
      'Shoulder injuries',
      'Post-surgical rehabilitation',
    ],
  },
  {
    slug: 'bhagalpur',
    name: 'Bhagalpur',
    state: 'Bihar',
    lat: 25.2425,
    lng: 86.9842,
    intro:
      'Bhagalpur is eastern Bihar\'s main city and referral centre, where specialist rehabilitation has historically been hard to find. Recovry-verified physiotherapy clinics here cover joint pain, spine care, paralysis rehab and recovery after fractures.',
    commonAilments: [
      'Knee and back pain',
      'Paralysis and stroke rehabilitation',
      'Post-fracture rehabilitation',
      'Cervical spondylosis',
      'Arthritis',
    ],
  },
  {
    slug: 'bihar-sharif',
    name: 'Bihar Sharif',
    state: 'Bihar',
    lat: 25.1982,
    lng: 85.5230,
    intro:
      'Bihar Sharif is the Nalanda district headquarters and a growing town about an hour from Patna, with limited but expanding access to rehabilitation. Recovry-verified physiotherapy clinics here treat joint pain, spine conditions and paralysis recovery.',
    commonAilments: [
      'Knee and back pain',
      'Paralysis rehabilitation',
      'Post-fracture rehabilitation',
      'Cervical pain',
      'Arthritis',
    ],
  },
];

export const CITY_SLUGS = CITIES.map((c) => c.slug);

/** Cities named in marketing copy. All have live clinics; keep it that way. */
const FEATURED_CITY_SLUGS = ['noida', 'delhi', 'gurgaon', 'mumbai', 'ahmedabad', 'lucknow', 'bengaluru', 'chandigarh'];

/** "22 cities, including Noida, Delhi, ..." - one sentence fragment for FAQs and intros. */
export function coverageSummary(): string {
  const names = FEATURED_CITY_SLUGS.map((s) => CITIES.find((c) => c.slug === s)?.name).filter(Boolean);
  return `${CITIES.length} cities across India, including ${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

export function findCity(slug: string | undefined): City | undefined {
  return CITIES.find((c) => c.slug === slug);
}

/** Directory of every city, grouped by state. Every city page links here once. */
export const CITIES_INDEX_PATH = '/physiotherapy-cities';

function distanceKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const rad = Math.PI / 180;
  const dLat = (b.lat - a.lat) * rad;
  const dLng = (b.lng - a.lng) * rad;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * rad) * Math.cos(b.lat * rad) * Math.sin(dLng / 2) ** 2;
  return 12742 * Math.asin(Math.sqrt(h));
}

/**
 * The closest other cities, for related-search links. Distance replaces a
 * hand-kept neighbour list so new cities slot in with no curation.
 */
export function nearestCities(from: City, limit = 10): City[] {
  return CITIES.filter((c) => c.slug !== from.slug)
    .map((c) => ({ c, d: distanceKm(from, c) }))
    .sort((a, b) => a.d - b.d)
    .slice(0, limit)
    .map((x) => x.c);
}

/** Cities grouped by state, states and cities both alphabetical. */
export function citiesByState(): Array<{ state: string; cities: City[] }> {
  const groups = new Map<string, City[]>();
  for (const c of CITIES) groups.set(c.state, [...(groups.get(c.state) ?? []), c]);
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([state, cities]) => ({ state, cities: [...cities].sort((a, b) => a.name.localeCompare(b.name)) }));
}
